import React from "react";

// Event questions shown at checkout. Defined at module level (not inside the page's render),
// so inputs keep focus while typing.

export const validateAnswers = (questions, answers) => {
  const errors = {};

  questions?.forEach((q) => {
    const answer = answers[q.id];
    const empty =
      !answer ||
      (typeof answer === "string" && answer.trim() === "") ||
      (Array.isArray(answer) && answer.length === 0);

    if (empty) {
      if (q.isRequired) errors[q.id] = "This field is required.";
      return;
    }

    switch (q.questionType) {
      case "EMAIL":
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(answer)) errors[q.id] = "Invalid email format.";
        break;
      case "PHONE":
        if (String(answer).replace(/[^0-9]/g, "").length !== 10)
          errors[q.id] = "Phone number must be 10 digits.";
        break;
      case "TEXT":
      case "TEXTAREA": {
        const length = String(answer).length;
        if (q.minLength && length < q.minLength) errors[q.id] = `Answer minimum length: ${q.minLength}.`;
        if (q.maxLength && length > q.maxLength) errors[q.id] = `Answer maximum length: ${q.maxLength}.`;
        break;
      }
      default:
        break;
    }
  });

  return errors;
};

const inputClass = (hasError) =>
  `w-full rounded-lg border p-3 text-gray-900 ${hasError ? "border-red-500 bg-red-50" : "border-gray-300"}`;

const INPUT_TYPES = { EMAIL: "email", PHONE: "tel", NUMBER: "number", DATE: "date" };

const Question = ({ q, index, value, error, onChange }) => {
  const id = `question-${q.id}`;
  let field;

  if (q.questionType === "TEXTAREA") {
    field = (
      <textarea id={id} rows={4} className={inputClass(error)} placeholder={q.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    );
  } else if (q.questionType === "SINGLE_CHOICE") {
    field = (
      <select id={id} className={inputClass(error)} value={value || ""} onChange={(e) => onChange(e.target.value)}>
        <option value="" disabled>
          Select an option
        </option>
        {(q.options || []).map((opt) => (
          <option key={opt} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  } else if (q.questionType === "MULTIPLE_CHOICE") {
    const selected = Array.isArray(value) ? value : [];
    field = (
      <div className="space-y-2" role="group" aria-labelledby={id}>
        {(q.options || []).map((opt) => (
          <label key={opt} className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={selected.includes(opt)}
              onChange={(e) => onChange(e.target.checked ? [...selected, opt] : selected.filter((o) => o !== opt))}
            />
            {opt}
          </label>
        ))}
      </div>
    );
  } else {
    field = (
      <input id={id} type={INPUT_TYPES[q.questionType] || "text"} className={inputClass(error)} placeholder={q.placeholder || ""} value={value || ""} onChange={(e) => onChange(e.target.value)} />
    );
  }

  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-sm font-medium text-gray-800">
        {index + 1}. {q.question}
        {q.isRequired && <span className="text-red-500"> *</span>}
      </label>
      {q.helpText && <p className="text-xs text-gray-500">{q.helpText}</p>}
      {field}
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
};

const EventQuestions = ({ questions, answers, errors, onChange }) => (
  <div className="space-y-4">
    {questions.map((q, index) => (
      <Question
        key={q.id}
        q={q}
        index={index}
        value={answers[q.id]}
        error={errors[q.id]}
        onChange={(value) => onChange(q.id, value)}
      />
    ))}
  </div>
);

export default EventQuestions;
